import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useTheme, buildUseStyles } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/styles';
import ListItem from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItem';
import ListItemAvatar from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItemAvatar';
import { Avatar } from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4';
import ListItemText from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/ListItemText';
import { DataValueMap } from 'Helpers/Testing';
import { FDIcon, FDPrimaryActionIconButton } from '@facet-wealth/design';
import Box from 'Components/ComponentLibrary/ExternalLibraries/Libraries/MaterialV4/Box';
import { noop } from 'Helpers/PropTypes';

const useStyles = buildUseStyles('ThreadParticipant', (theme) => ({
  root: {
    width: '100%',
    borderRadius: theme.radius.md,
  },
  avatar: {
    minWidth: 48, // MUI sets min width to 56 but that leaves a 16px gap, we want 8px 56 - 8 = 48
  },
  name: {
    fontWeight: theme.typography.fontWeightBold,
  },
  addIcon: {
    marginRight: theme.spacing(2),
    padding: theme.spacing(0.25),
    borderRadius: theme.radius.full,
    border: `2px solid ${theme.palette.facet.oceana500}`,
  },
}));

const ThreadParticipant = ({
  className,
  avatarSrc,
  name,
  role,
  canRemove,
  onRemove,
  canAdd,
  onAdd,
  canAssign,
  assignElement,
}) => {
  const theme = useTheme();
  const classes = useStyles(theme);
  return (
    <React.Fragment>
      <ListItem
        className={classNames(classes.root, className)}
        {...DataValueMap.init('messages-page-expanded-thread-participant').build()}
        button={canAdd}
        onClick={onAdd}
      >
        { canAdd ? (
          <Box className={classes.addIcon}>
            <FDIcon icon={'add'} color={'textSecondary'} />
          </Box>
        ) : (
          <ListItemAvatar className={classes.avatar}>
            <Avatar
              src={avatarSrc}
              alt={name}
            />
          </ListItemAvatar>
        )}
        <ListItemText
          primary={name}
          primaryTypographyProps={{
            className: classes.name,
            variant: 'h6',
            noWrap: true,
          }}
          secondary={role}
          secondaryTypographyProps={{
            className: classes.role,
            variant: 'body1',
            noWrap: true,
          }}
        />
        {canRemove ? (
          <FDPrimaryActionIconButton icon={'delete'} onClick={onRemove} size={'sm'} label={'Remove'} />
        ) : null
        }
        {canAssign ? assignElement : null}
      </ListItem>
    </React.Fragment>
  );
};

ThreadParticipant.defaultProps = {
  className: '',
  avatarSrc: '',
  name: '[Unnamed]',
  role: '',
  canRemove: false,
  onRemove: noop,
  canAdd: false,
  onAdd: noop,
  canAssign: false,
  assignElement: null,
};

ThreadParticipant.propTypes = {
  className: PropTypes.string,
  avatarSrc: PropTypes.string,
  name: PropTypes.string,
  role: PropTypes.string,
  canRemove: PropTypes.bool,
  onRemove: PropTypes.func,
  canAdd: PropTypes.bool,
  onAdd: PropTypes.func,
  canAssign: PropTypes.bool,
  assignElement: PropTypes.element,
};

export { ThreadParticipant };
